import dayjs from "dayjs";
import { useLayoutEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	ChannelsComplexFilter,
	ComplexFilterItem,
	ComplexFilterType,
	DateComplexFilter,
	PropertyIdsComplexFilter,
} from "interfaces/filters";

import {
	getDashboardFilter,
	getDashboardReport,
	getDashboardYearReport,
	setDashboardFilter,
} from "state/modules/dashboard";

import { getPropertiesList } from "state/modules/properties";
import { SlidersHorizontal } from "lucide-react";
import { subDays, startOfMonth, endOfMonth, subMonths, startOfYear, subYears, endOfYear } from "date-fns";
import {
	Badge,
	MultiSelect as UIMultiSelect,
	Button,
	DateRangePicker,
	useIsMobile,
} from "@hotelise/ui";
import type { DateRange, DateRangePreset } from "@hotelise/ui";

const channelOptions = [
	{ id: "airbnb-official", label: "Airbnb" },
	{ id: "booking", label: "Booking" },
	{ id: "manual", label: "Manual" },
	{ id: "homeaway", label: "Vrbo" },
];
const allChannelIds = channelOptions.map((c) => c.id);

const getPresets = (): DateRangePreset[] => {
	const now = new Date();
	return [
		{ label: "Last 30 days", from: subDays(now, 30), to: now },
		{ label: "Last month", from: startOfMonth(subMonths(now, 1)), to: endOfMonth(subMonths(now, 1)) },
		{ label: "YTD", from: startOfYear(now), to: now },
		{ label: "Last year", from: startOfYear(subYears(now, 1)), to: endOfYear(subYears(now, 1)) },
	];
};

const DashboardFilter = (): JSX.Element => {
	const dispatch = useDispatch();
	const filters = useSelector(getDashboardFilter);

	const propertiesList = useSelector(getPropertiesList);
	const properties = useMemo(
		() => propertiesList
			.filter((p) => p.id)
			.map((p) => ({ id: p.id, label: p.name || "-" })),
		[propertiesList]
	);

	const [filtersOpen, setFiltersOpen] = useState(false);
	const [activePreset, setActivePreset] = useState<string | null>(null);
	const isMobile = useIsMobile();
	const presets = getPresets();

	const defaultFilters: ComplexFilterItem[] = useMemo(() => {
		const today = dayjs();
		const startOfThisMonth = today.startOf("month");
		const endOfThisMonth = today.endOf("month");

		return [
			{
				type: ComplexFilterType.DATE,
				value: {
					dateFrom: startOfThisMonth.toISOString(),
					dateTo: endOfThisMonth.toISOString(),
				},
			} as DateComplexFilter,
			{ type: ComplexFilterType.PROPERTY_ID, value: [] } as PropertyIdsComplexFilter,
			{ type: ComplexFilterType.CHANNEL, value: [] } as ChannelsComplexFilter,
		];
	}, []);

	useLayoutEffect(() => {
		dispatch(setDashboardFilter(defaultFilters));
	}, [dispatch, defaultFilters]);

	const dateFilter = filters.find((f) => f.type === ComplexFilterType.DATE) as DateComplexFilter;
	const propertyFilter = filters.find((f) => f.type === ComplexFilterType.PROPERTY_ID) as PropertyIdsComplexFilter;
	const channelFilter = filters.find((f) => f.type === ComplexFilterType.CHANNEL) as ChannelsComplexFilter;

	const updateFilter = useCallback(
		(updatedFilter: ComplexFilterItem) => {
			const updatedFilters = filters.map((f) =>
				f.type === updatedFilter.type ? updatedFilter : f
			);
			dispatch(setDashboardFilter(updatedFilters));
			dispatch(getDashboardReport());
			if ([ComplexFilterType.PROPERTY_ID, ComplexFilterType.CHANNEL].includes(updatedFilter.type)) {
				dispatch(getDashboardYearReport());
			}
		},
		[filters, dispatch]
	);

	const handleDateChange = useCallback(
		(range: DateRange) => {
			if (!dateFilter) return;
			setActivePreset(null);
			const updatedFilter: DateComplexFilter = {
				...dateFilter,
				value: {
					...dateFilter.value!,
					dateFrom: range.from ? range.from.toISOString() : dateFilter.value?.dateFrom ?? null,
					dateTo: range.to ? range.to.toISOString() : null,
				},
			};
			const updatedFilters = filters.map((f) =>
				f.type === updatedFilter.type ? updatedFilter : f
			);
			dispatch(setDashboardFilter(updatedFilters));
			if (range.from && range.to) {
				dispatch(getDashboardReport());
			}
		},
		[dateFilter, filters, dispatch]
	);

	const handlePresetClick = useCallback(
		(preset: DateRangePreset) => {
			if (!dateFilter) return;
			setActivePreset(preset.label);
			updateFilter({
				...dateFilter,
				value: {
					...dateFilter.value!,
					dateFrom: preset.from.toISOString(),
					dateTo: preset.to.toISOString(),
				},
			});
		},
		[dateFilter, updateFilter]
	);

	const handleReset = useCallback(() => {
		setActivePreset(null);
		dispatch(setDashboardFilter(defaultFilters));
		dispatch(getDashboardReport());
		dispatch(getDashboardYearReport());
	}, [dispatch, defaultFilters]);

	const handleChangeProperty = (selectedIds: string[]) => {
		updateFilter({
			type: ComplexFilterType.PROPERTY_ID,
			value: selectedIds.includes("all") ? [] : selectedIds,
		});
	};

	const handleChangeChannel = (selectedIds: string[]) => {
		updateFilter({
			type: ComplexFilterType.CHANNEL,
			value: selectedIds.includes("all") || selectedIds.length === 0 ? [] : selectedIds,
		});
	};

	const selectedPropertyIds = propertyFilter?.value?.length ? propertyFilter.value : ["all"];
	const selectedChannels =
		!channelFilter?.value?.length || allChannelIds.every((id) => channelFilter.value.includes(id))
			? ["all"]
			: channelFilter.value;

	const propertyOptions = useMemo(() => [{ id: "all", label: "All Properties" }, ...properties], [properties]);
	const channelFilterOptions = useMemo(() => [{ id: "all", label: "All Channels" }, ...channelOptions], []);

	const dateRangeValue: DateRange = {
		from: dateFilter?.value?.dateFrom ? new Date(dateFilter.value.dateFrom) : undefined,
		to: dateFilter?.value?.dateTo ? new Date(dateFilter.value.dateTo) : undefined,
	};

	const activeFilterCount =
		(selectedPropertyIds[0] !== "all" ? 1 : 0) +
		(selectedChannels[0] !== "all" ? 1 : 0);

	if (isMobile) {
		return (
			<div className="flex flex-col gap-2 p-4 bg-card rounded-lg border border-border relative z-10 mb-4">
				<div className="flex flex-wrap gap-1.5">
					{presets.map((preset) => (
						<Badge
							key={preset.label}
							variant={activePreset === preset.label ? "secondary" : "outline"}
							className="cursor-pointer hover:bg-accent transition-colors px-2 py-0.5 text-xs"
							onClick={() => handlePresetClick(preset)}
						>
							{preset.label}
						</Badge>
					))}
				</div>

				<div className="flex items-center gap-2">
					<DateRangePicker
						value={dateRangeValue}
						onChange={handleDateChange}
						presets={presets}
						numberOfMonths={1}
						className="flex-1 w-auto text-xs h-8"
					/>

					<Button
						variant={filtersOpen ? "secondary" : "outline"}
						size="sm"
						className="h-8 px-2.5 shrink-0"
						onClick={() => setFiltersOpen(!filtersOpen)}
					>
						<SlidersHorizontal className="h-3.5 w-3.5" />
						{activeFilterCount > 0 && (
							<span className="ml-1 text-[10px] bg-primary text-primary-foreground rounded-full h-4 w-4 flex items-center justify-center">
								{activeFilterCount}
							</span>
						)}
					</Button>
				</div>

				{filtersOpen && (
					<div className="flex flex-col gap-2">
						<div className="flex gap-2">
							<UIMultiSelect
								items={propertyOptions}
								selectedItems={selectedPropertyIds}
								onSelectionChange={handleChangeProperty}
								placeholder="Property"
								className="flex-1"
								renderSelectedTags={false}
							/>
							<UIMultiSelect
								items={channelFilterOptions}
								selectedItems={selectedChannels}
								onSelectionChange={handleChangeChannel}
								placeholder="Channel"
								className="flex-1"
								renderSelectedTags={false}
							/>
						</div>
						<div className="flex gap-2">
							<Button variant="ghost" size="sm" onClick={handleReset}>
								Reset
							</Button>
						</div>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3 p-4 bg-card rounded-lg border border-border">
			<div className="flex flex-wrap gap-1.5">
				{presets.map((preset) => (
					<Badge
						key={preset.label}
						variant={activePreset === preset.label ? "secondary" : "outline"}
						className="cursor-pointer hover:bg-accent transition-colors px-2 py-0.5 text-xs"
						onClick={() => handlePresetClick(preset)}
					>
						{preset.label}
					</Badge>
				))}
			</div>

			<div className="flex flex-wrap items-start gap-4">
				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Date range</span>
					<div>
						<DateRangePicker
							value={dateRangeValue}
							onChange={handleDateChange}
							presets={presets}
							numberOfMonths={2}
							className="w-[260px]"
						/>
					</div>
				</div>

				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Property</span>
					<UIMultiSelect
						items={propertyOptions}
						selectedItems={selectedPropertyIds}
						onSelectionChange={handleChangeProperty}
						placeholder="Select properties..."
						className="w-[200px]"
						renderSelectedTags={false}
					/>
					{selectedPropertyIds[0] !== "all" && (
						<div className="flex flex-wrap gap-1 mt-1 w-[200px]">
							{selectedPropertyIds.filter((id) => id !== "all").map((id) => {
								const label = propertyOptions.find((p) => p.id === id)?.label || id;
								return (
									<Badge key={id} variant="secondary" className="text-xs">
										{label}
										<button type="button" aria-label={`Remove ${label}`} className="ml-1 leading-none hover:opacity-80" onClick={() => { const next = selectedPropertyIds.filter((s) => s !== id); handleChangeProperty(next.length === 0 ? ["all"] : next); }}>&times;</button>
									</Badge>
								);
							})}
						</div>
					)}
				</div>

				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">Channel</span>
					<UIMultiSelect
						items={channelFilterOptions}
						selectedItems={selectedChannels}
						onSelectionChange={handleChangeChannel}
						placeholder="Select channels..."
						className="w-[180px]"
						renderSelectedTags={false}
					/>
					{selectedChannels[0] !== "all" && (
						<div className="flex flex-wrap gap-1 mt-1 w-[180px]">
							{selectedChannels.filter((id) => id !== "all").map((id) => {
								const label = channelFilterOptions.find((c) => c.id === id)?.label || id;
								return (
									<Badge key={id} variant="secondary" className="text-xs">
										{label}
										<button type="button" aria-label={`Remove ${label}`} className="ml-1 leading-none hover:opacity-80" onClick={() => { const next = selectedChannels.filter((s) => s !== id); handleChangeChannel(next.length === 0 ? ["all"] : next); }}>&times;</button>
									</Badge>
								);
							})}
						</div>
					)}
				</div>

				<div className="space-y-1">
					<span className="text-xs font-medium text-muted-foreground">&nbsp;</span>
					<div className="flex items-center h-9">
						<Button variant="ghost" size="sm" onClick={handleReset}>
							Reset
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DashboardFilter;
